import { useEffect, useMemo, useState } from 'react';
import to from 'await-to-js';
import { apiClient } from 'c-data';
import type { Campaign } from 'c-sdk';
import type { SettledTarget } from 'c-main/Lib';
import { parseReportDate, usesInsertionOrderTargeting } from 'c-main/Lib';
import type { TargetFilterState, TargetSeriesParam } from './targetPacing';
import {
    dailyExpectedFromSummary,
    dailyImpactsFromStats,
    insertionOrderFieldFilters,
    lastSettledDay,
    targetExpectedWindow,
    targetFilterState,
    targetHeadline,
} from './targetPacing';

type Params = {
    campaign: Pick<
        Campaign,
        'id' | 'type' | 'date_start' | 'date_end' | 'playout_stats' | 'playout_percentage'
    > | null;
    /** DSP reporting is on (not a linear campaign, no linear override). */
    isDsp: boolean;
    advancedFilters: Record<string, unknown> | null | undefined;
    /** The user's date filter, when one is set. */
    dateFilter: { start: Date; end: Date } | null;
    environment: string | null | undefined;
    source: string | undefined;
    /** The page's field filters; only the insertion-order filter is carried over. */
    fieldFilters: Record<string, string[]> | null | undefined;
    /** The main summary's `daily_stats`: the delivery the filtered headline is judged on. */
    dailyStats: Record<string, { impacts?: unknown } | null | undefined> | null | undefined;
    /** Injected for tests; defaults to now. */
    today?: Date;
};

export type InsertionOrderTargets = {
    /** A `vistar_us2` campaign with DSP reporting: target pacing replaces even pacing. */
    isTargetCampaign: boolean;
    state: TargetFilterState;
    /** Targets can be drawn and quoted (target campaign, insertion order / date filters only). */
    showTargets: boolean;
    /** Target campaign under another filter: no expected figures, and the note says why. */
    targetsHidden: boolean;
    /** `expected_impacts_in_schedule` by day through the campaign end; null until loaded. */
    dailyExpected: Record<string, number> | null;
    /** For `PCATotalsByMetric`; null for every other type. */
    headline: SettledTarget | 'hidden' | null;
    /** For `useCampaignAnalyticsWidgetData`; null for every other type. */
    targetSeries: TargetSeriesParam | null;
    /** Changes when the series does, so fetched widgets redraw their expected line. */
    seriesKey: string;
};

/**
 * UL-8133 -- everything the analytics page needs for a `vistar_us2` campaign's targets, shared by
 * `CampaignAnalyticsES.tsx` and `CampaignAnalyticsES.main.tsx`.
 *
 * The expected series is a request of its own, with explicit dates (the campaign's, or the user's
 * date filter) and only the user's insertion-order filter. The page's delivery requests are left
 * exactly as they were, so delivery still counts settled rows only. Every other type makes no
 * request here and gets nulls back.
 */
export const useInsertionOrderTargets = ({
    campaign,
    isDsp,
    advancedFilters,
    dateFilter,
    environment,
    source,
    fieldFilters,
    dailyStats,
    today,
}: Params): InsertionOrderTargets => {
    const isTargetCampaign = isDsp && usesInsertionOrderTargeting(campaign);
    const state = targetFilterState(advancedFilters, dateFilter != null);
    const showTargets = isTargetCampaign && state !== 'otherFilter';

    const filterStart = dateFilter?.start.getTime();
    const filterEnd = dateFilter?.end.getTime();
    const dateWindow = useMemo(
        () =>
            targetExpectedWindow({
                campaignStart: parseReportDate(campaign?.date_start) ?? null,
                campaignEnd: parseReportDate(campaign?.date_end) ?? null,
                dateFilter:
                    filterStart != null && filterEnd != null
                        ? { start: new Date(filterStart), end: new Date(filterEnd) }
                        : null,
            }),
        [campaign?.date_end, campaign?.date_start, filterEnd, filterStart],
    );

    const payloadKey = useMemo(() => {
        if (!showTargets || !dateWindow || campaign?.id == null) return null;
        return JSON.stringify({
            campaignId: campaign.id,
            startDate: dateWindow.startDate,
            endDate: dateWindow.endDate,
            environment: environment ?? undefined,
            source,
            fieldFilters: insertionOrderFieldFilters(fieldFilters),
        });
    }, [campaign?.id, dateWindow, environment, fieldFilters, showTargets, source]);

    const [dailyExpected, setDailyExpected] = useState<Record<string, number> | null>(null);

    useEffect(() => {
        setDailyExpected(null);
        if (payloadKey == null) return undefined;

        let cancelled = false;
        const { request, controller } = apiClient.ReportsElasticSearch.summary({
            ...JSON.parse(payloadKey),
            showStats: true,
        });

        (async () => {
            const [err, response] = await to(request);
            if (cancelled) return;
            // A failed request shows no target rather than a wrong one.
            setDailyExpected(err ? null : dailyExpectedFromSummary(response?.data));
        })();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [payloadKey]);

    const todayTime = (today ?? new Date()).setHours(0, 0, 0, 0);
    const headline = useMemo(() => {
        if (!isTargetCampaign) return null;
        return targetHeadline({
            state,
            campaign,
            dailyExpected,
            dailyDelivered: dailyImpactsFromStats(dailyStats),
            lastSettled: lastSettledDay(new Date(todayTime)),
        });
    }, [campaign, dailyExpected, dailyStats, isTargetCampaign, state, todayTime]);

    const targetSeries = useMemo<TargetSeriesParam | null>(
        () => (isTargetCampaign ? { dailyExpected, showTargets } : null),
        [dailyExpected, isTargetCampaign, showTargets],
    );

    const seriesKey = useMemo(() => {
        if (!dailyExpected) return 'none';
        const days = Object.keys(dailyExpected);
        const total = days.reduce((sum, day) => sum + dailyExpected[day], 0);
        return `${days.length}:${total}`;
    }, [dailyExpected]);

    return {
        isTargetCampaign,
        state,
        showTargets,
        targetsHidden: isTargetCampaign && state === 'otherFilter',
        dailyExpected,
        headline,
        targetSeries,
        seriesKey,
    };
};
